import React, { createContext, useContext } from "react"
import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip"
import { Popover as PopoverPrimitive } from "@base-ui/react/popover"
import { popupTransition } from "../utils/popup-transition"
import { darkPopupSurface, darkPopupArrow } from "../utils/dark-popup"

// =============================================================================
// Constants
// =============================================================================
const DEFAULT_DELAY = 200

// =============================================================================
// Props
// =============================================================================

export type TooltipProviderProps = {
  /** Tooltip content */
  children?: React.ReactNode
  /** How long to wait before opening a tooltip (ms) */
  delay?: number
  /** How long to wait before closing a tooltip (ms) */
  closeDelay?: number
  /** Adjacent tooltip opens instantly if previous closed within this timeout (ms) */
  timeout?: number
}

export type TooltipRootProps = {
  /** The tooltip trigger and content */
  children?: React.ReactNode
  /** Whether the tooltip is initially open */
  defaultOpen?: boolean
  /** Controlled open state */
  open?: boolean
  /** Called when the tooltip is opened or closed */
  onOpenChange?: (open: boolean) => void
  /** Called after any open/close animations complete */
  onOpenChangeComplete?: (open: boolean) => void
  /** Whether the tooltip is disabled */
  disabled?: boolean
  /**
   * Prevent the tooltip contents from being hovered — the tooltip closes as
   * soon as the trigger is no longer hovered, rather than staying open while
   * the cursor moves onto the popup.
   */
  disableHoverablePopup?: boolean
  /**
   * Render as a standalone "infotip" — a dedicated control whose only purpose is
   * to reveal information. Switches the internals to a Base UI Popover with
   * `openOnHover`
   */
  standalone?: boolean
}

export type TooltipTriggerProps = {
  /** The trigger content */
  children?: React.ReactNode
  /** Replace the default button element with your own */
  render?: React.ReactElement
  /** Per-trigger delay override (ms) */
  delay?: number
  /** Per-trigger close delay override (ms) */
  closeDelay?: number
  /** Whether the tooltip should close when this trigger is clicked */
  closeOnClick?: boolean
  /** Disables the trigger without applying the disabled attribute */
  disabled?: boolean
  /** Additional CSS classes for the trigger element */
  className?: string
}

export type TooltipContentProps = {
  /** Gap in pixels between the trigger and tooltip */
  sideOffset?: number
  /** Maximum width of the tooltip content in pixels */
  maxWidth?: number
  /** Which side of the trigger the tooltip appears on */
  position?: "top" | "bottom" | "left" | "right"
  /** Alignment of the tooltip relative to the trigger */
  align?: "start" | "center" | "end"
  /** Whether to show the arrow pointing to the trigger */
  showArrow?: boolean
  /** Content displayed inside the tooltip */
  children: React.ReactNode
}

// =============================================================================
// Standalone mode
// =============================================================================

// A "standalone" tooltip is an infotip: a dedicated control (typically an
// info icon button) whose sole purpose is to reveal supplementary information.
// A plain Tooltip is meant to annotate a control with an unrelated purpose, so
// for an infotip we swap the internals to a Base UI Popover with `openOnHover`.
//
// The mode is set on the root via the `standalone` prop and read by Trigger and
// Content through context so the whole compound component stays in sync.
//
// PopoverPrimitive.Root has no `disabled` prop, so in standalone mode the
// root's `disabled` is threaded through context to Trigger, which forwards it
// to PopoverPrimitive.Trigger instead.
type StandaloneContextValue = {
  standalone: boolean
  disabled?: boolean
}

const StandaloneContext = createContext<StandaloneContextValue>({
  standalone: false
})

// =============================================================================
// Components
// =============================================================================

export function TooltipProvider({
  delay = DEFAULT_DELAY,
  children,
  closeDelay,
  timeout
}: TooltipProviderProps) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delay={delay}
      closeDelay={closeDelay}
      timeout={timeout}
    >
      {children}
    </TooltipPrimitive.Provider>
  )
}

export function TooltipRoot({
  children,
  defaultOpen,
  open,
  onOpenChange,
  onOpenChangeComplete,
  disabled,
  disableHoverablePopup = false,
  standalone = false
}: TooltipRootProps) {
  if (standalone) {
    return (
      <StandaloneContext.Provider value={{ standalone: true, disabled }}>
        <PopoverPrimitive.Root
          defaultOpen={defaultOpen}
          open={open}
          onOpenChange={onOpenChange}
          onOpenChangeComplete={onOpenChangeComplete}
        >
          {children}
        </PopoverPrimitive.Root>
      </StandaloneContext.Provider>
    )
  }

  return (
    <StandaloneContext.Provider value={{ standalone: false }}>
      <TooltipPrimitive.Root
        data-slot="tooltip"
        defaultOpen={defaultOpen}
        open={open}
        onOpenChange={onOpenChange}
        onOpenChangeComplete={onOpenChangeComplete}
        disabled={disabled}
        disableHoverablePopup={disableHoverablePopup}
      >
        {children}
      </TooltipPrimitive.Root>
    </StandaloneContext.Provider>
  )
}

export function TooltipTrigger({
  children,
  render,
  delay,
  closeDelay,
  closeOnClick,
  disabled,
  className
}: TooltipTriggerProps) {
  const { standalone, disabled: rootDisabled } = useContext(StandaloneContext)

  if (standalone) {
    return (
      <PopoverPrimitive.Trigger
        data-slot="tooltip-trigger"
        render={render}
        openOnHover
        delay={delay ?? DEFAULT_DELAY}
        closeDelay={closeDelay}
        disabled={disabled ?? rootDisabled}
        className={className}
      >
        {children}
      </PopoverPrimitive.Trigger>
    )
  }

  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      render={render}
      delay={delay}
      closeDelay={closeDelay}
      closeOnClick={closeOnClick}
      disabled={disabled}
      className={className}
    >
      {children}
    </TooltipPrimitive.Trigger>
  )
}

const styles = {
  popup: [
    // layout & positioning
    "inline-flex w-fit items-center gap-2",
    // appearance (shared dark surface) + tooltip-specific padding/weight
    darkPopupSurface,
    "px-4 py-2 font-medium",
    // entry/exit transition
    popupTransition()
  ].join(" "),
  arrow: darkPopupArrow
}

export function TooltipContent({
  sideOffset = 8,
  maxWidth = 512,
  position = "top",
  align = "center",
  showArrow = false,
  children
}: TooltipContentProps) {
  const { standalone } = useContext(StandaloneContext)

  if (standalone) {
    return (
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Positioner
          sideOffset={sideOffset}
          side={position}
          align={align}
          className="isolate z-[5002]"
        >
          <PopoverPrimitive.Popup
            data-slot="tooltip-content"
            className={styles.popup}
            style={{ maxWidth }}
          >
            {children}
            {showArrow && <PopoverPrimitive.Arrow className={styles.arrow} />}
          </PopoverPrimitive.Popup>
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    )
  }

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        sideOffset={sideOffset}
        side={position}
        align={align}
        className="isolate z-[5002]"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={styles.popup}
          style={{ maxWidth }}
        >
          {children}
          {showArrow && <TooltipPrimitive.Arrow className={styles.arrow} />}
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

// =============================================================================
// Compound component
// =============================================================================

interface TooltipComponent {
  (props: TooltipRootProps): React.JSX.Element
  Provider: React.ComponentType<TooltipProviderProps>
  Trigger: React.ComponentType<TooltipTriggerProps>
  Content: React.ComponentType<TooltipContentProps>
}

TooltipRoot.displayName = "Tooltip"
TooltipProvider.displayName = "Tooltip.Provider"
TooltipTrigger.displayName = "Tooltip.Trigger"
TooltipContent.displayName = "Tooltip.Content"

export const Tooltip: TooltipComponent = Object.assign(TooltipRoot, {
  Provider: TooltipProvider,
  Trigger: TooltipTrigger,
  Content: TooltipContent
} as const)
